import React, { useState, useEffect } from 'react';

export default function ImageManagement() {
  const [images, setImages] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [usageDetails, setUsageDetails] = useState(null);

  useEffect(() => {
    fetchImages();
    fetchStats();
  }, []);

  const fetchImages = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://chatpanel.my.id/api/images');
      const data = await response.json();
      if (data.success) {
        setImages(data.data);
      }
    } catch (error) {
      console.error('Error fetching images:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch('https://chatpanel.my.id/api/images/stats/storage');
      const data = await response.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchUsageDetails = async (filename) => {
    try {
      const response = await fetch(`https://chatpanel.my.id/api/images/${filename}/usage`);
      const data = await response.json();
      if (data.success) {
        setUsageDetails(data.data);
      }
    } catch (error) {
      console.error('Error fetching usage details:', error);
    }
  };

  const handleDeleteImage = async (filename) => {
    if (!window.confirm(`Are you sure you want to delete "${filename}"?`)) {
      return;
    }

    try {
      const response = await fetch(`https://chatpanel.my.id/api/images/${filename}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      
      if (data.success) {
        alert('Image deleted successfully!');
        fetchImages();
        fetchStats();
        setSelectedImage(null);
      } else {
        alert(data.error || 'Failed to delete image');
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('Failed to delete image');
    }
  };

  const handleCleanup = async () => {
    if (!window.confirm('This will delete all unused images. Continue?')) {
      return;
    }

    try {
      const response = await fetch('https://chatpanel.my.id/api/images/cleanup', {
        method: 'POST'
      });
      const data = await response.json();
      
      if (data.success) {
        alert(`Successfully cleaned up ${data.count} unused images!`);
        fetchImages();
        fetchStats();
      }
    } catch (error) {
      console.error('Error cleaning up images:', error);
      alert('Failed to cleanup images');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const openImageModal = (image) => {
    setSelectedImage(image);
    fetchUsageDetails(image.filename);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '48px', height: '48px', border: '4px solid #f3f4f6', borderTop: '4px solid #667eea', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }}></div>
          <p style={{ color: '#666' }}>Loading images...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '8px' }}>Image Management</h1>
          <p style={{ color: '#666' }}>Manage uploaded carousel images</p>
        </div>
        <button
          onClick={handleCleanup}
          style={{ padding: '10px 20px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}
        >
          🗑️ Cleanup Unused
        </button>
      </div>

      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '32px' }}>
          <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Total Images</p>
            <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#667eea' }}>{stats.totalImages}</p>
          </div>
          <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Used Images</p>
            <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#10b981' }}>{stats.usedImages}</p>
          </div>
          <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Unused Images</p>
            <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#f59e0b' }}>{stats.unusedImages}</p>
          </div>
          <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Total Storage</p>
            <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#8b5cf6' }}>{stats.totalSizeMB} MB</p>
          </div>
        </div>
      )}

      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', padding: '24px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '20px' }}>All Images ({images.length})</h2>
        
        {images.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            <p>No images uploaded yet</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '20px' }}>
            {images.map((image) => (
              <div
                key={image.filename}
                style={{ border: '2px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden', cursor: 'pointer', transition: 'all 0.2s' }}
                onClick={() => openImageModal(image)}
              >
                <div style={{ height: '200px', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                  <img
                    src={`https://chatpanel.my.id${image.url}`}
                    alt={image.filename}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
                <div style={{ padding: '12px' }}>
                  <p style={{ fontSize: '14px', fontWeight: '500', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {image.filename}
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                    <span>{formatFileSize(image.size)}</span>
                    <span>{formatDate(image.uploadedAt)}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ padding: '4px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: '500', background: image.isUsed ? '#d1fae5' : '#fee2e2', color: image.isUsed ? '#065f46' : '#991b1b' }}>
                      {image.isUsed ? `✓ Used (${image.usageCount})` : '✗ Unused'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedImage && (
        <div
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}
          onClick={() => setSelectedImage(null)}
        >
          <div
            style={{ background: 'white', borderRadius: '12px', maxWidth: '800px', width: '100%', maxHeight: '90vh', overflow: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ fontSize: '24px', fontWeight: 'bold' }}>Image Details</h2>
                <button
                  onClick={() => setSelectedImage(null)}
                  style={{ padding: '8px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '24px' }}
                >
                  ×
                </button>
              </div>

              <div style={{ marginBottom: '20px', borderRadius: '12px', overflow: 'hidden', background: '#f3f4f6' }}>
                <img
                  src={`https://chatpanel.my.id${selectedImage.url}`}
                  alt={selectedImage.filename}
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>Information</h3>
                <table style={{ width: '100%', fontSize: '14px' }}>
                  <tbody>
                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '8px 0', color: '#666', width: '140px' }}>Filename</td>
                      <td style={{ padding: '8px 0', fontWeight: '500' }}>{selectedImage.filename}</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '8px 0', color: '#666' }}>File Size</td>
                      <td style={{ padding: '8px 0', fontWeight: '500' }}>{formatFileSize(selectedImage.size)}</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '8px 0', color: '#666' }}>Uploaded</td>
                      <td style={{ padding: '8px 0', fontWeight: '500' }}>{formatDate(selectedImage.uploadedAt)}</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '8px 0', color: '#666' }}>Status</td>
                      <td style={{ padding: '8px 0' }}>
                        <span style={{ padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '500', background: selectedImage.isUsed ? '#d1fae5' : '#fee2e2', color: selectedImage.isUsed ? '#065f46' : '#991b1b' }}>
                          {selectedImage.isUsed ? `Used in ${selectedImage.usageCount} carousel(s)` : 'Not used'}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 0', color: '#666' }}>URL</td>
                      <td style={{ padding: '8px 0', fontWeight: '500', fontSize: '12px', wordBreak: 'break-all' }}>
                        {selectedImage.url}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {usageDetails && usageDetails.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>Used In</h3>
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                    {usageDetails.map((usage) => (
                      <div key={usage.id} style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>
                        <p style={{ fontWeight: '500', marginBottom: '4px' }}>{usage.title}</p>
                        <p style={{ fontSize: '12px', color: '#666' }}>
                          {usage.menu_name} → {usage.sub_menu_name}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setSelectedImage(null)}
                  style={{ padding: '10px 20px', background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}
                >
                  Close
                </button>
                {!selectedImage.isUsed && (
                  <button
                    onClick={() => handleDeleteImage(selectedImage.filename)}
                    style={{ padding: '10px 20px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}
                  >
                    Delete Image
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
